import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { ArrowLeft, ServerCrash, TriangleAlert } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';

import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { Sparkline, type SparkPoint } from '../components/Sparkline';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../features/auth/AuthProvider';
import { Occupancy } from '../features/platform/Occupancy';
import {
  formatPercent,
  getNodeStats,
  heldLine,
  localLines,
  sinceLabel,
  liveSummary,
  networkOverhead,
  nodeStatsKey,
  plural,
  statsSlots,
  statusLabels,
  streamCamera,
  streamVideo,
  warningText,
  type NodeHeavyStream,
  type NodeStats,
  type NodeStatsPoint,
  type NodeSystemStats,
} from '../features/platform/mediaNodes';
import { formatBitrate, formatBytes, formatUptime } from '../features/system/media';
import { ApiError } from '../lib/api';
import { useTimeFormat } from '../lib/useTimeFormat';

const statusTone = { active: 'ok', paused: 'warn', disabled: 'neutral' } as const;

/** A machine number over the last hour; points without the agent's numbers are gaps. */
function series(
  points: NodeStatsPoint[],
  pick: (s: NodeSystemStats) => number | null,
): SparkPoint[] {
  return points.map((p) => ({ at: Date.parse(p.at), value: p.system ? pick(p.system) : null }));
}

/** The input beyond the video over the last hour; points without both are gaps. */
const overheadSeries = (points: NodeStatsPoint[]): SparkPoint[] =>
  points.map((p) => ({ at: Date.parse(p.at), value: networkOverhead(p)?.bytes ?? null }));

/** kbps as the page's other bit rates ("4.2 Mbit/s"). */
const formatKbps = (kbps: number) => formatBitrate((kbps * 1000) / 8);

/** null (a gap, a dash) when the agent reported no total. */
const memoryPercent = (s: NodeSystemStats) =>
  s.memory_total_bytes > 0 ? (s.memory_used_bytes / s.memory_total_bytes) * 100 : null;

const isGone = (err: unknown) => err instanceof ApiError && err.status === 404;

/** "HH:MM" in the organisation's time zone. */
function useHourMinute() {
  const { timeZone } = useTimeFormat();
  return useMemo(() => {
    const f = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone });
    return (iso: string) => f.format(new Date(iso));
  }, [timeZone]);
}

/** "HH:MM" for today, "DD/MM HH:MM" for another day, in the organisation's time zone. */
function useSince() {
  const { timeZone } = useTimeFormat();
  return useMemo(() => (iso: string) => sinceLabel(iso, new Date(), timeZone), [timeZone]);
}

/**
 * One media node's operation (ADR-051): health, occupancy, the machine's
 * numbers over the last hour (from its agent), its video and its warnings.
 * Refreshed every 15 s, the period the central collects at.
 */
export function MediaNodePage() {
  const { id = '' } = useParams();
  const { request } = useAuth();
  const q = useQuery({
    queryKey: nodeStatsKey(id),
    queryFn: () => getNodeStats(request, id),
    refetchInterval: 15_000,
  });
  return (
    <>
      <PageHeader
        title={(!isGone(q.error) && q.data?.node.name) || 'Máquina de vídeo'}
        description="Operação da máquina na última hora: números da máquina, vídeo e avisos. Atualiza a cada 15 s."
        actions={
          <Link
            to="/media-nodes"
            className="inline-flex items-center gap-1.5 text-[13px] text-fg-muted hover:text-fg"
          >
            <ArrowLeft aria-hidden="true" className="size-4" strokeWidth={2} />
            Todas as máquinas
          </Link>
        }
      />
      <div className="space-y-4 p-4 sm:p-6">
        <Body q={q} />
      </div>
    </>
  );
}

function Body({ q }: { q: UseQueryResult<NodeStats> }) {
  const hhmm = useHourMinute();
  if (q.isPending) {
    return (
      <div role="status" className="h-40 animate-pulse rounded-lg border border-line bg-surface">
        <span className="sr-only">Carregando…</span>
      </div>
    );
  }
  // Deleted while open: the cached numbers no longer belong to anything.
  if (isGone(q.error)) {
    return (
      <EmptyState
        icon={ServerCrash}
        title="Esta máquina não existe mais"
        description="Ela foi apagada ou o endereço está errado. Volte à lista de máquinas."
      />
    );
  }
  if (q.isError && !q.data) {
    return (
      <EmptyState
        tone="crit"
        icon={ServerCrash}
        title="Não foi possível carregar os dados da máquina"
        description="A API não respondeu a esta consulta. Tente de novo em instantes."
        action={
          <Button
            variant="primary"
            onClick={() => {
              void q.refetch();
            }}
          >
            Tentar novamente
          </Button>
        }
      />
    );
  }
  return (
    <>
      {q.isError && (
        <p
          role="status"
          className="flex items-center gap-2 rounded-lg border border-warn/30 bg-warn-soft px-4 py-2.5 text-[13px] text-fg"
        >
          <TriangleAlert aria-hidden="true" className="size-4 shrink-0 text-warn" strokeWidth={2} />
          {`Não foi possível atualizar; mostrando os dados de ${hhmm(q.data.now)}.`}
        </p>
      )}
      <NodeView s={q.data} />
    </>
  );
}

function NodeView({ s }: { s: NodeStats }) {
  const hhmm = useHourMinute();
  const since = useSince();
  const n = s.node;
  const last = s.points.at(-1);
  // The newest point that has each part: one missed collection must not
  // blank the cards; an older reading says when it is from.
  const sysAt = s.points.findLast((p) => p.system !== null);
  const videoAt = s.points.findLast((p) => p.video !== null);
  const sys = sysAt?.system ?? null;
  const video = videoAt?.video ?? null;
  const sysStale = sysAt && sysAt !== last ? `Última leitura às ${hhmm(sysAt.at)}` : null;
  const videoStale = videoAt && videoAt !== last ? `Última leitura às ${hhmm(videoAt.at)}` : null;
  const mem = sys ? memoryPercent(sys) : null;
  // The newest point with both readings: the input beyond the video.
  const bothAt = s.points.findLast((p) => p.system !== null && p.video !== null);
  const overhead = bothAt ? networkOverhead(bothAt) : null;
  const held = sys ? heldLine(sys.hold) : null;
  const local = sys ? localLines(sys.local, since) : [];
  return (
    <>
      <section
        aria-label="Situação"
        className="flex flex-wrap items-end gap-x-8 gap-y-3 rounded-lg border border-line bg-surface px-4 py-3"
      >
        <Fact label="Situação">
          <div className="flex gap-1.5">
            <StatusBadge tone={statusTone[n.status]}>{statusLabels[n.status]}</StatusBadge>
            <StatusBadge tone={n.up ? 'ok' : 'warn'}>
              {n.up ? 'Disponível' : 'Indisponível'}
            </StatusBadge>
          </div>
        </Fact>
        <Fact label="Servidor de vídeo">
          <span className="font-mono text-xs">{s.engine_version || '—'}</span>
        </Fact>
        <Fact label="Máquina no ar há">{sys ? formatUptime(sys.uptime_seconds) : '—'}</Fact>
        <Fact label="Ocupação">
          <Occupancy node={n} />
        </Fact>
      </section>

      {s.warnings.length > 0 && (
        <section
          aria-label="Avisos da máquina"
          className="overflow-hidden rounded-lg border border-warn/30 bg-warn-soft"
        >
          <ul aria-label="Avisos" className="text-[13px] text-fg">
            {s.warnings.map((w) => {
              // The disk full of evidence is the one red warning.
              const crit = w.code === 'spool_full_evidence';
              return (
                <li
                  key={w.code}
                  className={`flex items-start gap-2 px-4 py-1.5 ${crit ? 'bg-crit-soft' : ''}`}
                >
                  <TriangleAlert
                    aria-hidden="true"
                    className={`mt-0.5 size-4 shrink-0 ${crit ? 'text-crit' : 'text-warn'}`}
                    strokeWidth={2}
                  />
                  {warningText(w)}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section
        aria-labelledby="node-machine"
        className="overflow-hidden rounded-lg border border-line bg-surface"
      >
        <SectionHeading id="node-machine" stale={sysStale}>
          Máquina
        </SectionHeading>
        {sys ? (
          <div className="grid grid-cols-1 gap-px bg-line sm:grid-cols-2 lg:grid-cols-4">
            <Card
              label="CPU"
              value={formatPercent(sys.cpu_percent)}
              detail={`${plural(sys.cpus, 'núcleo', 'núcleos')} · carga ${sys.load1.toFixed(2)} ${sys.load5.toFixed(2)} ${sys.load15.toFixed(2)}`}
            >
              <Sparkline
                label="CPU na última hora"
                points={series(s.points, (x) => x.cpu_percent)}
                slots={statsSlots}
                max={100}
                format={formatPercent}
              />
            </Card>
            <Card
              label="Memória"
              value={mem === null ? '—' : formatPercent(mem)}
              detail={`${formatBytes(sys.memory_used_bytes)} de ${formatBytes(sys.memory_total_bytes)}`}
            >
              <Sparkline
                label="Memória na última hora"
                points={series(s.points, memoryPercent)}
                slots={statsSlots}
                max={100}
                format={formatPercent}
              />
            </Card>
            <Card
              label="Rede"
              value={formatBitrate(
                sys.network_in_bytes_per_second + sys.network_out_bytes_per_second,
              )}
              detail={`${formatBitrate(sys.network_in_bytes_per_second)} entrada · ${formatBitrate(sys.network_out_bytes_per_second)} saída`}
            >
              <Trend name="Entrada">
                <Sparkline
                  label="Entrada na última hora"
                  points={series(s.points, (x) => x.network_in_bytes_per_second)}
                  slots={statsSlots}
                  format={formatBitrate}
                />
              </Trend>
              <Trend name="Saída">
                <Sparkline
                  label="Saída na última hora"
                  points={series(s.points, (x) => x.network_out_bytes_per_second)}
                  slots={statsSlots}
                  format={formatBitrate}
                />
              </Trend>
            </Card>
            <Card
              label="Pasta temporária"
              value={formatBytes(sys.spool_bytes)}
              detail={`${plural(sys.spool_files, 'arquivo', 'arquivos')} esperando envio · ${formatBytes(sys.disk_free_bytes)} livres`}
            >
              <Sparkline
                label="Pasta temporária na última hora"
                points={series(s.points, (x) => x.spool_bytes)}
                slots={statsSlots}
                format={formatBytes}
              />
              {held && <p className="text-2xs text-fg-muted">{held}</p>}
              {local.map((line) => (
                <p key={line} className="text-2xs text-fg-muted">
                  {line}
                </p>
              ))}
            </Card>
            <Card
              className="sm:col-span-2 lg:col-span-4"
              label="Entrada além do vídeo"
              value={overhead ? formatBitrate(overhead.bytes) : '—'}
              detail={
                overhead
                  ? overhead.percent === null
                    ? 'sem entrada na rede'
                    : `${formatPercent(overhead.percent)} da entrada`
                  : 'sem leitura do vídeo e da máquina ao mesmo tempo'
              }
            >
              <Sparkline
                label="Entrada além do vídeo na última hora"
                points={overheadSeries(s.points)}
                slots={statsSlots}
                format={formatBitrate}
              />
              <p className="text-2xs text-fg-subtle">
                Rede de entrada da máquina menos o vídeo recebido das câmeras. Acima de 25% por 5
                min indica perda de pacotes no caminho (as câmeras reenviam) ou tráfego
                desconhecido.
              </p>
            </Card>
          </div>
        ) : (
          <p className="px-4 py-6 text-[13px] text-fg-muted">
            {s.agent
              ? 'Sem dados: o agente desta máquina ainda não respondeu, está fora do ar ou é anterior à versão 0.53.0.'
              : 'Sem dados: esta máquina não tem agente (instalação única); só o vídeo aparece abaixo.'}
          </p>
        )}
      </section>

      <section
        aria-labelledby="node-video"
        className="overflow-hidden rounded-lg border border-line bg-surface"
      >
        <SectionHeading id="node-video" stale={videoStale}>
          Vídeo
        </SectionHeading>
        <dl className="grid grid-cols-2 gap-px bg-line sm:grid-cols-3 lg:grid-cols-6">
          <Figure label="Streams" value={video ? String(video.streams) : '—'} />
          <Figure label="Espectadores" value={video ? String(video.viewers) : '—'} />
          <Figure label="Gravações em andamento" value={video ? String(video.recordings) : '—'} />
          <Figure
            label="Banda de entrada"
            value={video ? formatBitrate(video.bytes_per_second) : '—'}
          />
          <Figure label="Câmeras atribuídas" value={String(s.cameras)} />
          <Figure label="Equipamentos" value={String(n.usage.equipment)} />
        </dl>
        <p className="border-t border-line px-4 py-2 text-xs text-fg-muted">
          {liveSummary(s.live)}
        </p>
      </section>

      <HeavyStreams streams={s.streams} heavyKbps={s.heavy_kbps} />
      <p className="text-2xs text-fg-subtle">
        {s.points.length === 0
          ? 'Sem dados ainda: o central coleta as máquinas no ar a cada 15 s.'
          : `Um ponto a cada ${String(s.interval_seconds)} s, guardado na memória do central por até 1 hora (reiniciar a API zera o histórico).`}
      </p>
    </>
  );
}

/** The node's 10 heaviest ingests now, any organisation's (ADR-051). */
function HeavyStreams({ streams, heavyKbps }: { streams: NodeHeavyStream[]; heavyKbps: number }) {
  const limit = `${String(heavyKbps)} kbps`;
  return (
    <section
      aria-labelledby="node-heavy"
      className="overflow-hidden rounded-lg border border-line bg-surface"
    >
      <SectionHeading id="node-heavy" stale={null}>
        Câmeras que mais consomem
      </SectionHeading>
      <p className="border-b border-line px-4 py-2 text-xs text-fg-muted">
        {`As 10 maiores taxas de entrada agora. Câmera acima de ${limit} costuma estar com o stream mal configurado (resolução, quadros por segundo ou taxa altos).`}
      </p>
      {streams.length === 0 ? (
        <p className="px-4 py-6 text-[13px] text-fg-muted">Nenhuma câmera aberta nesta máquina.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <caption className="sr-only">Câmeras que mais consomem nesta máquina</caption>
            <thead className="text-2xs tracking-wide text-fg-subtle uppercase">
              <tr className="border-b border-line">
                <th scope="col" className="px-4 py-2 font-medium">
                  Câmera
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Empresa
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Resolução
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  Entrada
                </th>
                <th scope="col" className="px-4 py-2 text-right font-medium">
                  Espectadores
                </th>
                <th scope="col" className="px-4 py-2 font-medium">
                  Gravando
                </th>
              </tr>
            </thead>
            <tbody>
              {streams.map((st, i) => (
                <tr
                  key={`${st.device_id}-${String(i)}`}
                  className="border-b border-line last:border-0"
                >
                  <td className="px-4 py-2.5">
                    <div className="text-fg">{streamCamera(st)}</div>
                    {st.site_name && <div className="text-2xs text-fg-subtle">{st.site_name}</div>}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-fg-muted">{st.tenant_name || '—'}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-fg-muted">{streamVideo(st)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="font-mono text-xs text-fg-muted tabular-nums">
                      {formatKbps(st.kbps)}
                    </div>
                    {st.heavy && <StatusBadge tone="warn">{`Acima de ${limit}`}</StatusBadge>}
                  </td>
                  <td className="px-4 py-2.5 text-right text-fg tabular-nums">{st.viewers}</td>
                  <td className="px-4 py-2.5 text-xs text-fg-muted">
                    {st.recording ? 'Sim' : 'Não'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SectionHeading({
  id,
  stale,
  children,
}: {
  id: string;
  stale: string | null;
  children: ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-line px-4 py-2.5">
      <h2 id={id} className="text-[13px] font-medium">
        {children}
      </h2>
      {stale && <span className="text-2xs text-fg-subtle">{stale}</span>}
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-2xs tracking-wide text-fg-subtle uppercase">{label}</div>
      <div className="mt-1 text-[13px] text-fg">{children}</div>
    </div>
  );
}

function Card({
  label,
  value,
  detail,
  className = '',
  children,
}: {
  label: string;
  value: string;
  detail: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`min-w-0 bg-surface px-4 py-3 ${className}`}>
      <span className="text-2xs tracking-wide text-fg-subtle uppercase">{label}</span>
      <div className="mt-1 text-xl font-semibold text-fg tabular-nums">{value}</div>
      <div className="mt-0.5 text-xs text-fg-muted">{detail}</div>
      <div className="mt-2 space-y-1">{children}</div>
    </div>
  );
}

/** One of several trends in a card, named on its left (the line alone does not say which). */
function Trend({ name, children }: { name: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2">
      <span aria-hidden="true" className="w-11 shrink-0 text-2xs text-fg-subtle">
        {name}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface px-4 py-3">
      <dt className="text-2xs tracking-wide text-fg-subtle uppercase">{label}</dt>
      <dd className="mt-1 text-lg font-semibold text-fg tabular-nums">{value}</dd>
    </div>
  );
}
